import React, { useState } from 'react';
import { marked } from 'marked';
import { useJobPoller, type GenerationConfig } from '../hooks/useJobPoller';
import { Loader2, CheckCircle, AlertCircle, Sparkles, Settings, Image, FileText, Globe } from 'lucide-react';

function renderMarkdown(content: string): string {
  if (!content) return '';
  const isHtml = content.trim().startsWith('<') || /<(p|div|h[1-6]|ul|ol|li)\b/i.test(content);
  if (isHtml) return content;
  try { return marked(content, { breaks: true, gfm: true }) as string; } catch { return content; }
}

interface BlogGeneratorProps {
  getAuthHeaders?: () => Promise<Record<string, string>>;
}

const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'authoritative', label: 'Authoritative' },
  { value: 'conversational', label: 'Conversational' },
];

const COUNTRY_OPTIONS = [
  { value: 'US', label: 'United States' },
  { value: 'UK', label: 'United Kingdom' },
  { value: 'CA', label: 'Canada' },
  { value: 'AU', label: 'Australia' },
  { value: 'IN', label: 'India' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
];

const WORD_COUNT_OPTIONS = [
  { value: 800, label: '800 words (Short)' },
  { value: 1200, label: '1,200 words (Medium)' },
  { value: 1500, label: '1,500 words (Standard)' },
  { value: 2000, label: '2,000 words (Long)' },
  { value: 2500, label: '2,500 words (Extended)' },
];

export const BlogGenerator: React.FC<BlogGeneratorProps> = ({ getAuthHeaders }) => {
  const [topic, setTopic] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [wordCount, setWordCount] = useState(1500);
  const [targetCountry, setTargetCountry] = useState('US');
  const [tone, setTone] = useState('professional');
  const [generateImages, setGenerateImages] = useState(true);
  const [imageCount, setImageCount] = useState(3);
  const [keywords, setKeywords] = useState('');

  const { startJob, jobState, isPolling, error, resetState } = useJobPoller();

  const handleGenerate = async () => {
    if (!topic.trim()) return;

    const config: GenerationConfig = {
      topic: topic.trim(),
      target_word_count: wordCount,
      target_country: targetCountry,
      generate_images: generateImages,
      image_count: imageCount,
      tone: tone,
      keywords: keywords ? keywords.split(',').map(k => k.trim()).filter(Boolean) : undefined,
    };

    try {
      const headers = getAuthHeaders ? await getAuthHeaders() : {};
      startJob(config, headers);
    } catch (err) {
      console.error('Failed to get auth headers:', err);
    }
  };

  const handleNewGeneration = () => {
    resetState();
    setTopic('');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'failed':
        return 'bg-red-500';
      default:
        return 'bg-blue-600';
    }
  };

  const getStepIcon = (step: string) => {
    if (step.toLowerCase().includes('image')) return <Image className="w-4 h-4" />;
    if (step.toLowerCase().includes('content') || step.toLowerCase().includes('writing')) return <FileText className="w-4 h-4" />;
    if (step.toLowerCase().includes('research')) return <Globe className="w-4 h-4" />;
    return <Sparkles className="w-4 h-4" />;
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-xl shadow-lg border border-gray-100">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-blue-600" />
          AI Blog Generator
        </h1>
        <p className="text-gray-500">Generate SEO-optimized content for your Wix blog.</p>
      </div>

      {/* Input Section */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            What should this blog post be about?
          </label>
          <textarea
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            disabled={isPolling}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-24 disabled:bg-gray-50 disabled:cursor-not-allowed"
            placeholder="e.g., The benefits of green tea for remote workers..."
          />
        </div>

        {/* Advanced Options Toggle */}
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          disabled={isPolling}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
        >
          <Settings className="w-4 h-4" />
          {showAdvanced ? 'Hide' : 'Show'} Advanced Options
        </button>

        {/* Advanced Options */}
        {showAdvanced && (
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-2 gap-4">
              {/* Word Count */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Word Count
                </label>
                <select
                  value={wordCount}
                  onChange={(e) => setWordCount(Number(e.target.value))}
                  disabled={isPolling}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  {WORD_COUNT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Target Country */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Country
                </label>
                <select
                  value={targetCountry}
                  onChange={(e) => setTargetCountry(e.target.value)}
                  disabled={isPolling}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  {COUNTRY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Tone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Writing Tone
                </label>
                <select
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  disabled={isPolling}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  {TONE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Image Count */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of Images
                </label>
                <select
                  value={imageCount}
                  onChange={(e) => setImageCount(Number(e.target.value))}
                  disabled={isPolling || !generateImages}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  {[1, 2, 3, 4, 5].map(n => (
                    <option key={n} value={n}>{n} image{n > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Generate Images Toggle */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="generateImages"
                checked={generateImages}
                onChange={(e) => setGenerateImages(e.target.checked)}
                disabled={isPolling}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="generateImages" className="text-sm text-gray-700">
                Generate AI images for the blog post
              </label>
            </div>

            {/* Keywords */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Keywords (comma-separated, optional)
              </label>
              <input
                type="text"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                disabled={isPolling}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="e.g., green tea, health benefits, productivity"
              />
            </div>
          </div>
        )}

        {/* Generate Button */}
        <button
          onClick={handleGenerate}
          disabled={isPolling || !topic.trim()}
          className={`w-full py-3 px-4 rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-colors
            ${isPolling ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}
            ${!topic.trim() && !isPolling ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isPolling ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Generating Content...
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5" />
              Generate Blog Post
            </>
          )}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-6 p-4 bg-red-50 text-red-700 rounded-lg flex items-start gap-3 border border-red-200">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium">Generation Failed</h3>
            <p className="text-sm mt-1">{error}</p>
            <button
              onClick={handleNewGeneration}
              className="mt-3 text-sm text-red-600 hover:text-red-800 underline"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Progress View */}
      {jobState && !error && (
        <div className="mt-8 border-t pt-6">
          <div className="flex justify-between items-center mb-2">
            <span className="font-medium text-gray-700 capitalize flex items-center gap-2">
              {getStepIcon(jobState.current_step)}
              Status: {jobState.status.replace(/_/g, ' ')}
            </span>
            <span className="text-sm text-gray-500">{jobState.progress_percent}%</span>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4 overflow-hidden">
            <div
              className={`h-2.5 rounded-full transition-all duration-500 ease-out ${getStatusColor(jobState.status)}`}
              style={{ width: `${jobState.progress_percent}%` }}
            ></div>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            {jobState.status !== 'completed' && jobState.status !== 'failed' && (
              <span className="inline-block animate-pulse">{jobState.current_step}</span>
            )}
          </p>

          {/* Completion State */}
          {jobState.status === 'completed' && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-green-800 font-medium mb-3">
                <CheckCircle className="w-5 h-5" />
                Generation Complete!
              </div>

              {/* Content Preview */}
              {jobState.content_preview && (
                <div
                  className="mb-4 p-3 bg-white rounded border border-green-100 text-sm text-gray-700 max-h-32 overflow-y-auto prose prose-sm"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(jobState.content_preview) }}
                />
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleNewGeneration}
                  className="flex-1 bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-50 transition-colors"
                >
                  Generate Another
                </button>
                {jobState.result_url && (
                  <a
                    href={jobState.result_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 text-center transition-colors"
                  >
                    View Generated Article
                  </a>
                )}
                <button className="flex-1 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors">
                  Publish to Wix
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BlogGenerator;
